// HU-23 · Parametrizar formato, plazo y firma por autoridad. F-05, Sprint 2.
//
// La pantalla que hace cierta la frase de RNF-08: "14 mercados parametrizables sin
// redesplegar". Hasta ahora `parametro_pais` se sembraba con la autoridad y los plazos en
// NULL y no habia forma de llenarlos que no fuera un UPDATE contra la base.
//
// TRES COSAS QUE ESTA PANTALLA HACE DISTINTO Y CONVIENE LEER UNA VEZ
// ------------------------------------------------------------------
// 1. NO PROPONE NINGUN PLAZO. Ni un placeholder con "3", ni un valor inicial. RN-12: los
//    valores los carga ILENDER y el sistema no trae ninguno. Un placeholder con un numero
//    se copia, y entonces el sistema habria "sugerido" una obligacion regulatoria.
//
// 2. DISTINGUE "SIN CONFIGURAR" DE "CONFIGURADO EN CERO". Son dos estados distintos y el
//    segundo es legitimo (cero = "el mismo dia"). Por eso los campos numericos van vacios
//    y no en 0, y la tabla escribe "sin configurar" en cursiva en vez de un guion.
//
// 3. SENALA EL CALENDARIO QUE FALTA. Una autoridad con plazo en dias habiles y sin
//    feriados cargados calcula, pero el resultado no es fiable — y HU-23 pide que eso "se
//    senale explicitamente" en vez de fingir precision.
//
// CC-02 (B2, AT S3 §11.3.1): UNA FILA POR AUTORIDAD, agrupadas por país. Un país puede
// tener varias (SENASA y SANIPES); la predeterminada va primero y es la que lleva lo que
// es del país (zona horaria, feriados, sección 11). Acciones por fila según lo que ofrece
// el servidor (`acciones_permitidas`); no hay «Eliminar». Spec: `ui/MG-ILE-49.md`.

import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  AlertTriangle, CalendarDays, CheckCheck, Clock, Landmark, PenLine, Plus, PowerOff, Power,
  Settings2, Star, TriangleAlert,
} from 'lucide-react';
import {
  Alert, Button, Card, DataTable, EmptyState, Field, Input, Modal, Mono,
  PageHeader, Select, StatusBadge, Textarea,
} from '../components/ui';
import * as api from '../api/autoridades';
import { ORIGENES_DE_COMPUTO, UNIDADES_DE_PLAZO } from '../api/autoridades';
import type { ActoSobreLaAutoridad } from '../api/autoridades';
import {
  ACTO_QUE_SE_CONFIRMA, EstadoDeLaAutoridad, ModalDeActoSobreAutoridad, ModalDeAltaDeAutoridad,
} from '../components/AutoridadesDelPais';
import { filasPorAutoridad } from '../lib/autoridadesDelPais';
import type { ReactNode } from 'react';
import type { ColumnaDeTabla } from '../components/ui';
import { aFallo } from '../api/cliente';
import type { Fallo } from '../api/cliente';
import type { RespuestaDelApi } from '../tipos';
import { opcionesDeZonaHoraria, zonasHorariasDeAmerica } from '../lib/zonasHorarias';

// Etiqueta de la unidad para la tabla. Cae al codigo si el backend devolviera una unidad
// que este front no conoce: mostrar el codigo es informacion, y esconderla no.
const etiquetaDeUnidad = (codigo: string): string =>
  UNIDADES_DE_PLAZO.find((u) => u.value === codigo)?.label ?? codigo;

const etiquetaDeOrigen = (codigo: string): string =>
  ORIGENES_DE_COMPUTO.find((o) => o.value === codigo)?.label ?? codigo;

// `null` y `''` son lo mismo aqui —el usuario vacio la casilla— y los dos significan SIN
// CONFIGURAR. `0` NO: es un plazo de cero dias, que es duro pero es una obligacion que
// una autoridad puede imponer. `Number('')` da 0, que es exactamente la confusion que
// esta funcion existe para evitar.
const aNumeroONulo = (texto: unknown): number | null => {
  const limpio = String(texto ?? '').trim();
  if (limpio === '') return null;
  const numero = Number(limpio);
  return Number.isFinite(numero) ? numero : null;
};

const aTexto = (numero: unknown): string => (numero === null || numero === undefined ? '' : String(numero));

/** «Bolivia (BO)»: el nombre para leer, el código para buscar. */
const nombreDelPais = (fila: RespuestaDelApi): string =>
  (fila.nombre_pais ? `${fila.nombre_pais} (${fila.pais})` : String(fila.pais));

// El formulario en su forma de PANTALLA (todo cadenas, que es lo que devuelven los
// inputs). La traduccion a la forma del API vive en `aCuerpoDelApi`, abajo.
const aFormulario = (fila: RespuestaDelApi): RespuestaDelApi => ({
  pais: fila.pais,
  nombre_pais: fila.nombre_pais,
  // CC-02: que autoridad del pais se edita. La predeterminada (o una fila sin codigo, de un
  // servidor anterior) va por la ruta del pais, que tambien lleva lo que es del pais.
  codigo: fila.codigo ?? null,
  es_predeterminada: fila.es_predeterminada !== false,
  nombre_de_la_predeterminada: fila.fila_del_pais?.autoridad ?? fila.autoridad,
  texto_de_plantilla: fila.texto_de_plantilla ?? null,
  autoridad: fila.autoridad ?? '',
  exige_profesional_responsable: fila.exige_profesional_responsable ? 'si' : 'no',
  origen_computo: fila.origen_computo ?? 'recepcion_ilender',
  plazo_grave: aTexto(fila.plazo_grave?.cantidad),
  plazo_no_grave: aTexto(fila.plazo_no_grave?.cantidad),
  // La unidad es UNA para los dos plazos: `parametro_pais` guarda una sola columna
  // `unidad_plazo`, y el umbral se expresa en esa misma unidad.
  unidad_plazo: fila.unidad_plazo ?? '',
  umbral_alerta: aTexto(fila.umbral_alerta),
  // --- HU-23 alcance ampliado (8-sep-2026) ---
  // C10: el segundo reloj. SIN valor por defecto, como los otros plazos (C2).
  plazo_informe_final: aTexto(fila.plazo_informe_final?.cantidad),
  umbral_informe_final: aTexto(fila.umbral_informe_final),
  // C4: la zona en la que se cuentan los dias habiles de ESTA autoridad.
  zona_horaria: fila.zona_horaria ?? '',
  // C9: el acuse se muestra como viene. Vacio SI significa algo: en la predeterminada, que
  // no se vigila; en las demas, que usa los dias de la predeterminada (RN-81). Pintar un 5
  // aqui lo volvia a escribir al guardar cualquier otro cambio.
  dias_alerta_acuse_demorado: aTexto(fila.dias_alerta_acuse_demorado),
  // El escalamiento es NOT NULL en la base: si la fila viene sin el, se muestra su default 2.
  dias_escalamiento_vencido: aTexto(fila.dias_escalamiento_vencido ?? 2),
  // C1: §11 y plantilla.
  exige_seccion_de_personas: fila.exige_seccion_de_personas ? 'si' : 'no',
  plantilla_carta_version: aTexto(fila.plantilla_carta_version),
  periodicidad_ips: fila.periodicidad_ips ?? '',
  // HU-21-C1 / HU-40: el firmante pre-configurado donde no se exige PRFV.
  firmante_responsable: fila.firmante_responsable ?? '',
});

/**
 * Formulario -> cuerpo del PUT.
 *
 * Un plazo viaja como par {cantidad, unidad} o no viaja: un numero sin unidad no dice si
 * son horas o dias habiles, y esa diferencia decide si la notificacion llego a tiempo. El
 * backend lo rechaza igual; mandarlo bien desde aqui es lo que hace que el error se vea
 * en el campo y no como un 422 generico.
 */
const aCuerpoDelApi = (formulario: RespuestaDelApi): Record<string, unknown> => {
  const unidad = formulario.unidad_plazo || null;
  const conUnidad = (valor: unknown) => {
    const cantidad = aNumeroONulo(valor);
    if (cantidad === null || !unidad) return null;
    return { cantidad, unidad };
  };
  return {
    autoridad: formulario.autoridad.trim(),
    exige_profesional_responsable: formulario.exige_profesional_responsable === 'si',
    origen_computo: formulario.origen_computo || 'recepcion_ilender',
    plazo_grave: conUnidad(formulario.plazo_grave),
    plazo_no_grave: conUnidad(formulario.plazo_no_grave),
    umbral_alerta: aNumeroONulo(formulario.umbral_alerta),
    plazo_informe_final: conUnidad(formulario.plazo_informe_final),
    umbral_informe_final: aNumeroONulo(formulario.umbral_informe_final),
    zona_horaria: formulario.zona_horaria.trim() || null,
    dias_alerta_acuse_demorado: aNumeroONulo(formulario.dias_alerta_acuse_demorado),
    // NOT NULL en la base: si el administrador lo vacia, vuelve al default 2, no a null.
    dias_escalamiento_vencido: aNumeroONulo(formulario.dias_escalamiento_vencido) ?? 2,
    exige_seccion_de_personas: formulario.exige_seccion_de_personas === 'si',
    // Viaja tal como llegó (la vigente que resolvió el servidor): el PUT sustituye la
    // configuración entera y la columna heredada no se vacía por accidente.
    plantilla_carta_version: aNumeroONulo(formulario.plantilla_carta_version),
    periodicidad_ips: formulario.periodicidad_ips.trim() || null,
    firmante_responsable: formulario.firmante_responsable.trim() || null,
  };
};

/**
 * Formulario -> cuerpo del PUT de UNA autoridad que no es la predeterminada (CC-02).
 *
 * Lo mismo que `aCuerpoDelApi` sin lo que es del país (zona horaria, sección 11, IPS,
 * plantilla): eso no es de esta autoridad y la ruta no lo acepta.
 */
const CAMPOS_DE_LA_AUTORIDAD = [
  'exige_profesional_responsable', 'origen_computo', 'plazo_grave', 'plazo_no_grave',
  'umbral_alerta', 'plazo_informe_final', 'umbral_informe_final',
  'dias_alerta_acuse_demorado', 'dias_escalamiento_vencido', 'firmante_responsable',
];

const aCuerpoDeLaAutoridad = (formulario: RespuestaDelApi): Record<string, unknown> => {
  const cuerpo = aCuerpoDelApi(formulario);
  return {
    nombre: cuerpo.autoridad,
    ...Object.fromEntries(CAMPOS_DE_LA_AUTORIDAD.map((campo) => [campo, cuerpo[campo]])),
  };
};

// Enteros no negativos en todo lo numerico: `min="0"` del input es decorativo (no hay
// <form>, la validacion nativa nunca corre) y `-5` o `2.5` viajarian a la API. Anota el
// problema en su campo, salvo que ya tenga uno mas concreto.
const senalarLosNoEnteros = (problemas: Record<string, string>, valores: Record<string, number | null>): void => {
  for (const [campo, valor] of Object.entries(valores)) {
    if (valor !== null && (!Number.isInteger(valor) || valor < 0) && !problemas[campo]) {
      problemas[campo] = 'Un número entero, nunca negativo.';
    }
  }
};

/**
 * Lo que la pantalla puede rechazar ANTES de llamar al API.
 *
 * Solo las dos reglas que el usuario puede corregir mirando el formulario. El resto de
 * invariantes las sostiene el dominio, y duplicarlas aqui las pondria en dos sitios que
 * se separan — que es el defecto que este proyecto ya pago con los 14 paises escritos en
 * `src/data/paises.js`.
 */
const problemasDelFormulario = (formulario: RespuestaDelApi): Record<string, string> => {
  const problemas: Record<string, string> = {};
  if (!formulario.autoridad.trim()) {
    problemas.autoridad = 'La autoridad rotula el desplegable del caso y encabeza la carta.';
  }
  const plazoGrave = aNumeroONulo(formulario.plazo_grave);
  const plazoNoGrave = aNumeroONulo(formulario.plazo_no_grave);
  const plazoInforme = aNumeroONulo(formulario.plazo_informe_final);
  const hayPlazo = plazoGrave !== null || plazoNoGrave !== null || plazoInforme !== null;
  if (hayPlazo && !formulario.unidad_plazo) {
    problemas.unidad_plazo = 'Un número sin unidad no es un plazo: elige horas, días hábiles o corridos.';
  }
  const umbral = aNumeroONulo(formulario.umbral_alerta);
  if (umbral !== null && plazoGrave === null && plazoNoGrave === null) {
    problemas.umbral_alerta = 'No se puede avisar antes de un plazo que no existe. Configura primero un plazo.';
  }
  // Un umbral mayor que el plazo es avisar antes de que llegue el reporte: la alerta
  // naceria vencida. El backend ya lo detecta (`el_umbral_supera_el_plazo`), pero lo
  // decia DESPUES de guardar, en la tabla, en 11 px ambar. Aqui se impide antes.
  const menorPlazo = Math.min(...[plazoGrave, plazoNoGrave].filter((n): n is number => n !== null));
  if (umbral !== null && Number.isFinite(menorPlazo) && umbral > menorPlazo) {
    problemas.umbral_alerta = `Avisar ${umbral} antes de un plazo de ${menorPlazo} es avisar antes de que llegue el reporte.`;
  }
  // C10: el umbral del informe final exige su propio plazo.
  const umbralInforme = aNumeroONulo(formulario.umbral_informe_final);
  if (umbralInforme !== null && plazoInforme === null) {
    problemas.umbral_informe_final = 'Configura primero el plazo del informe final.';
  }
  if (umbralInforme !== null && plazoInforme !== null && umbralInforme > plazoInforme) {
    problemas.umbral_informe_final = `Avisar ${umbralInforme} antes de un plazo de ${plazoInforme} es avisar antes del día cero.`;
  }
  senalarLosNoEnteros(problemas, {
    plazo_grave: plazoGrave, plazo_no_grave: plazoNoGrave, plazo_informe_final: plazoInforme,
    umbral_alerta: umbral, umbral_informe_final: umbralInforme,
    dias_alerta_acuse_demorado: aNumeroONulo(formulario.dias_alerta_acuse_demorado),
    dias_escalamiento_vencido: aNumeroONulo(formulario.dias_escalamiento_vencido),
  });
  return problemas;
};

// "3 días hábiles" o el aviso de que nadie lo configuro. En cursiva y con texto, no con
// un guion: un guion se lee como "no aplica", y lo que pasa es que falta el dato.
/** Un plazo regulatorio: cuanto y en que unidad. No es un numero suelto. */
type PlazoRegulatorio = { cantidad: number; unidad: string };

function Plazo({ plazo }: { plazo?: PlazoRegulatorio | null }) {
  if (!plazo) return <em className="text-ink-soft text-meta">sin configurar</em>;
  return (
    <span className="text-body">
      <strong className="font-semibold">{plazo.cantidad}</strong>{' '}
      <span className="text-ink-soft">{etiquetaDeUnidad(plazo.unidad).toLowerCase()}</span>
    </span>
  );
}

// COMO SE DIBUJA CADA COLUMNA DE LA TABLA.
// Estaban las siete encadenadas en un `if (columna === ...)` dentro del componente,
// y la funcion cruzo el limite de complejidad cognitiva que vigila el analisis de
// calidad. Separadas, cada columna se lee y se cambia sin releer las otras seis.
// Viven fuera del componente porque ninguna necesita su estado: solo la fila.
/** Una autoridad con su configuracion de plazos, tal como llega del servidor. */
type FilaDeAutoridad = RespuestaDelApi;

// RN-05 / RN-78. Sale de la base y no de un `if pais === 'PE'`. CC-02: el texto lo da el
// servidor (`texto_de_firma`, «Exige firma del PRFV»); el de respaldo es el de siempre.
const CELDA_DE_FIRMA = (fila: FilaDeAutoridad): ReactNode => (fila.exige_profesional_responsable ? (
  <StatusBadge tone="ai" icon={PenLine} data-testid="autoridades-fila-firma">{fila.texto_de_firma ?? 'Firma el PRFV'}</StatusBadge>
) : (
  <StatusBadge tone="neutral" icon={PenLine} data-testid="autoridades-fila-firma">{fila.texto_de_firma ?? 'Firma interna'}</StatusBadge>
));

const CELDA_POR_COLUMNA: Record<string, (fila: FilaDeAutoridad) => ReactNode> = {
  // CC-02: el codigo (que no se edita) junto al pais cuando difiere del nombre, y el estado
  // de la autoridad con texto: predeterminada, activa o desactivada, por confirmar.
  autoridad: (fila: FilaDeAutoridad) => (
    <div className="space-y-1">
      <p className="font-semibold text-ink text-body">
        {fila.nombre_pais ?? fila.pais} · {fila.autoridad}
      </p>
      <Mono className="text-meta text-ink-soft">
        {fila.pais}{fila.codigo && fila.codigo !== fila.autoridad ? ` · ${fila.codigo}` : ''}
      </Mono>
      {fila.codigo && <EstadoDeLaAutoridad fila={fila} />}
    </div>
  ),

  // CC2-23-C1: una autoridad sin plazo que no es la predeterminada dice de quien lo toma
  // («Sin plazo configurado: se usa el de SAG»); el texto lo da el servidor.
  plazo_grave: (fila: FilaDeAutoridad) => (fila.texto_de_plazo && !fila.es_predeterminada ? (
    <em className="text-ink-soft text-meta">{fila.texto_de_plazo}</em>
  ) : <Plazo plazo={fila.plazo_grave} />),

  plazo_no_grave: (fila: FilaDeAutoridad) => <Plazo plazo={fila.plazo_no_grave} />,

  umbral: (fila: FilaDeAutoridad) => {
    if (fila.umbral_alerta === null || fila.umbral_alerta === undefined) {
      return <em className="text-ink-soft text-meta">sin configurar</em>;
    }
    return (
      <span className="text-body">
        {fila.umbral_alerta} {etiquetaDeUnidad(fila.unidad_plazo).toLowerCase()}
        {fila.el_umbral_supera_el_plazo && (
          <span className="block text-meta text-amber-700">supera el plazo</span>
        )}
      </span>
    );
  },

  origen: (fila: FilaDeAutoridad) => (
    <span className="text-meta">{etiquetaDeOrigen(fila.origen_computo)}</span>
  ),

  // RN-05. Sale de la base y no de un `if pais === 'PE'`: el dia que Peru exija PRFV
  // se cambia el dato y no se redespliega la SPA.

  // CC-02: la plantilla vigente de la autoridad, o de quien la toma prestada (RN-81). Va
  // bajo la firma, en la misma columna: con una columna más, a 1366 las acciones quedaban
  // fuera de la vista.
  firma_y_plantilla: (fila: FilaDeAutoridad) => (
    <div className="space-y-1">
      {CELDA_DE_FIRMA(fila)}
      <p className="text-meta text-ink-soft" data-testid="autoridades-fila-plantilla">
        {fila.texto_de_plantilla ?? 'Sin plantilla'}
      </p>
    </div>
  ),

  calendario: (fila: FilaDeAutoridad) => {
    const anios = fila.anios_con_calendario ?? [];
    if (anios.length === 0) {
      // Sin calendario solo es un problema si el plazo se cuenta en días hábiles (RN-13).
      const enDiasHabiles = fila.unidad_plazo === 'dias_habiles';
      return (
        <StatusBadge tone={enDiasHabiles ? 'warning' : 'neutral'}>
          Sin cargar
        </StatusBadge>
      );
    }
    return <Mono className="text-meta">{anios.join(', ')}</Mono>;
  },
};

/** El aviso de lo hecho, con el nombre de la autoridad y del país. */
const textoDelActo = (r: RespuestaDelApi, acto: ActoSobreLaAutoridad): string => {
  const pais = r.nombre_pais ?? r.pais;
  const textos: Record<ActoSobreLaAutoridad, string> = {
    desactivar: `${r.nombre} desactivada: ya no se propone en los casos nuevos.`,
    reactivar: `${r.nombre} reactivada.`,
    marcar_predeterminada: `${r.nombre} es ahora la autoridad predeterminada de ${pais}.`,
    confirmar: `${r.nombre} confirmada.`,
  };
  return textos[acto];
};

/** Lo que el servidor permite sobre la fila, con su etiqueta y su ícono. Sin «Eliminar». */
const BOTONES_DE_ACTO: { acto: ActoSobreLaAutoridad; etiqueta: string; icono: typeof Power; testid: string }[] = [
  { acto: 'confirmar', etiqueta: 'Confirmar', icono: CheckCheck, testid: 'autoridades-fila-confirmar' },
  { acto: 'marcar_predeterminada', etiqueta: 'Marcar como predeterminada', icono: Star, testid: 'autoridades-fila-predeterminada' },
  { acto: 'desactivar', etiqueta: 'Desactivar', icono: PowerOff, testid: 'autoridades-fila-desactivar' },
  { acto: 'reactivar', etiqueta: 'Reactivar', icono: Power, testid: 'autoridades-fila-reactivar' },
];

function AccionesDeLaFila({ fila, ocupada, onConfigurar, onFeriados, onActuar }: {
  fila: RespuestaDelApi;
  ocupada: boolean;
  onConfigurar: () => void;
  onFeriados: () => void;
  onActuar: (acto: ActoSobreLaAutoridad) => void;
}) {
  const permitidas: string[] = Array.isArray(fila.acciones_permitidas) ? fila.acciones_permitidas : [];
  // Los feriados son del PAÍS: se cargan desde la fila de su predeterminada.
  const delPais = fila.es_predeterminada !== false;
  return (
    <div className="flex flex-col items-end gap-1.5">
      <Button size="sm" variant="ghost" icon={Settings2} onClick={onConfigurar} data-testid="autoridades-fila-configurar">
        Configurar
      </Button>
      {delPais && (
        <Button size="sm" variant="ghost" icon={CalendarDays} onClick={onFeriados} data-testid="autoridades-fila-feriados">
          Feriados
        </Button>
      )}
      {BOTONES_DE_ACTO.filter((b) => permitidas.includes(b.acto)).map((b) => (
        <Button key={b.acto} size="sm" variant="ghost" icon={b.icono} disabled={ocupada}
          onClick={() => onActuar(b.acto)} data-testid={b.testid}>
          {b.etiqueta}
        </Button>
      ))}
    </div>
  );
}

export default function AutoridadesYPlazos() {
  const [filas, setFilas] = useState<RespuestaDelApi[] | null>(null);
  const [cargando, setCargando] = useState(true);
  const [fallo, setFallo] = useState<Fallo | null>(null);
  const [editando, setEditando] = useState<RespuestaDelApi | null>(null);
  const [problemas, setProblemas] = useState<Record<string, string>>({});
  const [guardando, setGuardando] = useState(false);
  const [errorDelFormulario, setErrorDelFormulario] = useState<Fallo | null>(null);
  const [calendarioDe, setCalendarioDe] = useState<RespuestaDelApi | null>(null);
  // CC-02: alta, acto pendiente de confirmar, acto en curso y aviso de lo hecho.
  const [agregando, setAgregando] = useState(false);
  const [pendiente, setPendiente] = useState<{ fila: RespuestaDelApi; acto: ActoSobreLaAutoridad } | null>(null);
  const [actuando, setActuando] = useState<string | null>(null);
  const [hecho, setHecho] = useState<string | null>(null);
  const [falloDelActo, setFalloDelActo] = useState<Fallo | null>(null);

  const recargar = useCallback(async () => {
    setCargando(true);
    setFallo(null);
    try {
      setFilas(await api.listarAutoridades());
    } catch (error) {
      setFallo(aFallo(error));
    } finally {
      setCargando(false);
    }
  }, []);

  useEffect(() => {
    recargar();
  }, [recargar]);

  // Las dos faltas que la HU manda hacer visibles, contadas arriba. Descubrirlas fila a
  // fila en una tabla de 14 es descubrirlas tarde.
  const sinAlertas = useMemo(
    () => (filas ?? []).filter((f) => !f.esta_configurada_para_alertar),
    [filas],
  );
  const sinCalendario = useMemo(
    () => (filas ?? []).filter(
      (f) => f.unidad_plazo === 'dias_habiles' && (f.anios_con_calendario ?? []).length === 0,
    ),
    [filas],
  );

  // Una fila por autoridad (CC-02). Los avisos de arriba siguen contando PAÍSES.
  const filasDeAutoridad = useMemo(() => filasPorAutoridad(filas ?? []), [filas]);

  const guardar = async () => {
    // El modal solo existe con un formulario abierto; decirlo explicito evita que un
    // cambio futuro llame a guardar sin el.
    if (!editando) return;
    const encontrados = problemasDelFormulario(editando);
    setProblemas(encontrados);
    if (Object.keys(encontrados).length > 0) return;
    setGuardando(true);
    setErrorDelFormulario(null);
    try {
      // La predeterminada va por la ruta del país, que lleva también lo que es del país
      // (zona, sección 11); cualquier otra, por la suya (CC-02).
      if (editando.es_predeterminada || !editando.codigo) {
        await api.configurarAutoridad(editando.pais, aCuerpoDelApi(editando));
      } else {
        await api.configurarAutoridadDelPais(editando.pais, editando.codigo, aCuerpoDeLaAutoridad(editando));
      }
      setHecho(null);
      setEditando(null);
      await recargar();
    } catch (error) {
      const fallo = aFallo(error);
      // HU-23-C4: la zona que el dominio rechaza («'X' no es una zona horaria IANA
      // valida…») se señala EN SU CAMPO. Salía como alerta general del modal, lejos del
      // control que hay que corregir, y `Field` ya tenía el carril del error cableado.
      if (fallo.estado === 422 && /zona horaria/i.test(fallo.message)) {
        setProblemas({ zona_horaria: fallo.message });
      } else {
        setErrorDelFormulario(fallo);
      }
    } finally {
      setGuardando(false);
    }
  };

  const alTerminarElActo = async (resultado: RespuestaDelApi, acto: ActoSobreLaAutoridad) => {
    setPendiente(null);
    setHecho(textoDelActo(resultado, acto));
    await recargar();
  };

  /**
   * Desactivar y cambiar la predeterminada se confirman (cambian lo que reciben los casos
   * nuevos); confirmar y reactivar van directo. Lo hecho se avisa arriba y la tabla se
   * vuelve a pedir al servidor.
   */
  const actuar = async (fila: RespuestaDelApi, acto: ActoSobreLaAutoridad) => {
    setHecho(null);
    setFalloDelActo(null);
    if (ACTO_QUE_SE_CONFIRMA(acto)) {
      setPendiente({ fila, acto });
      return;
    }
    setActuando(String(fila.id));
    try {
      const resultado = await api.cambiarEstadoDeAutoridad(String(fila.pais), String(fila.codigo), acto);
      await alTerminarElActo(resultado, acto);
    } catch (error) {
      setFalloDelActo(aFallo(error));
    } finally {
      setActuando(null);
    }
  };

  const columnas = [
    { key: 'autoridad', label: 'País · autoridad' },
    { key: 'plazo_grave', label: 'Plazo EA grave', testid: 'autoridades-fila-plazo' },
    { key: 'plazo_no_grave', label: 'Plazo EA no grave' },
    { key: 'umbral', label: 'Avisa antes' },
    { key: 'origen', label: 'Cuenta desde' },
    { key: 'firma_y_plantilla', label: 'Firma y plantilla' },
    { key: 'calendario', label: 'Calendario hábil' },
    { key: 'acciones', label: '' },
  ];

  // Las columnas que solo dependen de la fila se dibujan fuera (CELDA_POR_COLUMNA).
  // La de acciones se queda aqui porque necesita los setters del componente.
  const celda = (fila: FilaDeAutoridad, { key: columna }: ColumnaDeTabla) => {
    const dibujar = CELDA_POR_COLUMNA[columna];
    if (dibujar) return dibujar(fila);
    return (
      <AccionesDeLaFila
        fila={fila}
        ocupada={actuando === String(fila.id)}
        onConfigurar={() => { setProblemas({}); setErrorDelFormulario(null); setEditando(aFormulario(fila)); }}
        onFeriados={() => setCalendarioDe(fila)}
        onActuar={(acto) => { actuar(fila, acto); }}
      />
    );
  };

  return (
    <>
      <PageHeader
        eyebrow="Administración · Autoridades y plazos"
        hu="HU-23 · F-05 · CC-02"
        title="Formato, plazo y firma por autoridad"
        subtitle="El plazo, el formato y la firma de cada autoridad. Un país puede tener varias; el sistema no trae plazos por defecto."
        icon={Landmark}
        actions={(
          <Button icon={Plus} onClick={() => { setHecho(null); setAgregando(true); }}
            disabled={cargando || !filas?.length} data-testid="autoridades-agregar">
            Agregar autoridad
          </Button>
        )}
      />

      {fallo && (
        <Alert tone="danger" title="No se pudo cargar la configuración de las autoridades" detalleTecnico={fallo.detalleTecnico} data-testid="autoridades-error">
          {fallo.message}
        </Alert>
      )}

      {hecho && (
        <Alert tone="success" title={hecho} onCerrar={() => setHecho(null)} data-testid="autoridades-exito" />
      )}

      {falloDelActo && (
        <Alert tone="danger" title="No se pudo completar" detalleTecnico={falloDelActo.detalleTecnico}
          onCerrar={() => setFalloDelActo(null)} data-testid="autoridades-acto-error">
          {falloDelActo.message}
        </Alert>
      )}

      {!cargando && sinAlertas.length > 0 && (
        <Alert
          tone="warning"
          title={`${sinAlertas.length} autoridad(es) no dispararán alertas de vencimiento`}
          icon={TriangleAlert}
        >
          Les falta el plazo o el umbral de aviso. Los casos de esos países{' '}
          <strong>avanzan igual</strong>, pero sin aviso antes de vencer:{' '}
          <span className="font-medium text-ink">{sinAlertas.map((f) => nombreDelPais(f)).join(' · ')}</span>
        </Alert>
      )}

      {!cargando && sinCalendario.length > 0 && (
        <Alert
          tone="warning"
          title="Hay plazos en días hábiles sin calendario de feriados cargado"
          icon={AlertTriangle}
        >
          Sin feriados, el cálculo descuenta solo sábados y domingos: la fecha real de
          vencimiento será esa o <strong>posterior</strong>, nunca anterior.{' '}
          <span className="font-medium text-ink">{sinCalendario.map((f) => nombreDelPais(f)).join(' · ')}</span>
        </Alert>
      )}

      <Card className="mt-4" pad={false}>
        {cargando && <p className="p-6 text-body text-ink-soft" data-testid="autoridades-cargando">Cargando autoridades…</p>}
        {!cargando && filasDeAutoridad.length > 0 && (
          <DataTable columns={columnas} rows={filasDeAutoridad} renderCell={celda} sinMarco
            data-testid="autoridades-tabla" testidDeFila="autoridades-fila" />
        )}
        {!cargando && !filas?.length && !fallo && (
          <EmptyState
            icon={Landmark}
            title="No hay autoridades parametrizadas"
            description="Falta la configuración inicial de los países. Avisa a soporte."
            data-testid="autoridades-vacio"
          />
        )}
      </Card>

      <ModalDeAltaDeAutoridad
        abierto={agregando}
        paises={filas ?? []}
        onCerrar={() => setAgregando(false)}
        onCreada={async (creada) => {
          setAgregando(false);
          setHecho(`${creada.nombre} agregada en ${creada.nombre_pais ?? creada.pais}. Carga sus plazos con «Configurar».`);
          await recargar();
        }}
      />

      <ModalDeActoSobreAutoridad
        pendiente={pendiente}
        onCerrar={() => setPendiente(null)}
        onHecho={alTerminarElActo}
      />

      <ModalDeConfiguracion
        editando={editando}
        problemas={problemas}
        error={errorDelFormulario}
        guardando={guardando}
        onCambio={(cambio) => setEditando((p) => ({ ...(p ?? {}), ...(cambio as Record<string, unknown>) }))}
        onCerrar={() => setEditando(null)}
        onGuardar={guardar}
      />

      {/* Solo se monta con una autoridad elegida: el modal necesita su pais para pedir
          los feriados, y sin el no hay nada que mostrar. */}
      {calendarioDe && <ModalDeFeriados
        autoridad={calendarioDe}
        onCerrar={() => setCalendarioDe(null)}
        onCargado={recargar}
      />}
    </>
  );
}

/** «Configurar Perú» para la predeterminada (como siempre); «Configurar SANIPES · Perú» para las demás. */
const tituloDelFormulario = (editando: RespuestaDelApi | null): string => {
  if (!editando) return 'Configurar autoridad';
  const pais = editando.nombre_pais ?? editando.pais;
  return editando.es_predeterminada === false ? `Configurar ${editando.autoridad} · ${pais}` : `Configurar ${pais}`;
};

/**
 * La plantilla vigente. Para una autoridad que no es la predeterminada, el texto del
 * servidor (CC-02: «Sin plantilla: se usa la de SENASA»); para la predeterminada, el de siempre.
 */
const textoDeLaPlantilla = (editando: RespuestaDelApi | null): string => {
  if (editando?.es_predeterminada === false && editando.texto_de_plantilla) {
    return String(editando.texto_de_plantilla);
  }
  return editando?.plantilla_carta_version
    ? `${editando.autoridad || editando.pais} · v${editando.plantilla_carta_version}`
    : 'Sin plantilla publicada';
};

/**
 * La ayuda del acuse demorado. Vacío significa cosas distintas según la autoridad: en la
 * predeterminada no se vigila; en las demás se usan los días de la predeterminada.
 */
const ayudaDelAcuse = (delPais: boolean): string => {
  const base = 'Días hábiles sin cargo tras enviar la carta. Por defecto, 5.';
  if (delPais) return `${base} Vacío = no se vigila`;
  return `${base} Vacío = usa los días de la autoridad predeterminada`;
};

function ModalDeConfiguracion({ editando, problemas, error, guardando, onCambio, onCerrar, onGuardar }: {
  editando: RespuestaDelApi | null;
  problemas: Record<string, string>;
  error?: Fallo | null;
  guardando: boolean;
  onCambio: (cambio: Record<string, unknown>) => void;
  onCerrar: () => void;
  onGuardar: () => void;
}) {
  // HU-23-C4 · LA ZONA SE ELIGE, NO SE TECLEA (15-sep-2026, validación del operador).
  // Quien configura la autoridad no tiene por qué saber que Bolivia es `America/La_Paz`,
  // y un nombre IANA mal escrito el dominio lo rechaza: el campo de texto dejaba al
  // administrador sin poder guardar. La lista la da el navegador (misma base IANA que
  // valida el backend, sin segunda lista a mano), acotada a América, y cada opción dice
  // la hora que es ahora en esa zona para poder compararla con el reloj del país. La
  // zona sembrada por el script 021 llega preseleccionada. Ver `lib/zonasHorarias.ts`.
  const zonas = useMemo(() => zonasHorariasDeAmerica(), []);
  const zonaActual = editando?.zona_horaria ?? '';
  const opcionesDeZona = useMemo(
    () => opcionesDeZonaHoraria(zonas, zonaActual),
    [zonas, zonaActual],
  );
  // CC-02: lo que es DEL PAÍS (zona horaria, sección 11, IPS) se edita solo desde su
  // autoridad predeterminada; las demás autoridades del país lo comparten.
  const delPais = editando?.es_predeterminada !== false;
  const pais = editando?.nombre_pais ?? editando?.pais;
  return (
    <Modal
      open={Boolean(editando)}
      onClose={onCerrar}
      sucio={Boolean(editando)}
      ancho="trabajo"
      eyebrow="Administración · Autoridades y plazos"
      title={tituloDelFormulario(editando)}
      icon={Settings2}
      data-testid="autoridades-modal-configurar"
      footer={
        <>
          <Button variant="secondary" onClick={onCerrar}>Cancelar</Button>
          <Button variant="primary" onClick={onGuardar} disabled={guardando} data-testid="autoridades-form-guardar">
            {guardando ? 'Guardando…' : 'Guardar configuración'}
          </Button>
        </>
      }
    >
      {error && (
        <Alert tone="danger" title="No se pudo guardar" detalleTecnico={error.detalleTecnico} data-testid="autoridades-form-error">{error.message}</Alert>
      )}
      {delPais ? (
        <Alert tone="info" title="Lo que se deje vacío queda SIN CONFIGURAR">
          Vaciar un plazo lo borra. Una autoridad sin plazo no dispara alertas de vencimiento.
        </Alert>
      ) : (
        <Alert tone="info" title={`Sin plazo, se usa el de ${editando?.nombre_de_la_predeterminada ?? 'la predeterminada'}`}>
          Vaciar un plazo lo borra. La zona horaria, los feriados y la sección 11 son de {pais}: se
          configuran en {editando?.nombre_de_la_predeterminada ?? 'su autoridad predeterminada'}.
        </Alert>
      )}

      {/* CUATRO GRUPOS CON LEYENDA (14-sep-2026). Eran diecisiete campos en una sola columna
          de 512 px: el administrador no sabía dónde acababan los plazos y empezaban las
          alertas. Cada `fieldset` agrupa lo que se decide junto. */}
      <div className="space-y-5 mt-2">
        <Field label="Autoridad" required
          hint={editando?.codigo && !delPais
            ? `Código ${editando.codigo}, no se cambia. El nombre encabeza la carta`
            : 'Rotula el desplegable del caso y encabeza la carta'}
          error={problemas.autoridad}>
          <Input
            value={editando?.autoridad ?? ''}
            onChange={(e) => onCambio({ autoridad: e.target.value })}
            data-testid="autoridades-form-nombre"
          />
        </Field>

        <fieldset className="border border-hairline rounded-xl p-4 space-y-4">
        <legend className="px-1.5 text-body font-semibold text-ink">Plazos ante la autoridad</legend>
        <Field
          label="Unidad del plazo"
          hint="La misma para los dos plazos y para el aviso"
          error={problemas.unidad_plazo}
        >
          <Select
            value={editando?.unidad_plazo ?? ''}
            onChange={(e) => onCambio({ unidad_plazo: e.target.value })}
            placeholder="Sin configurar"
            options={[
              { value: '', label: 'Sin configurar' },
              ...UNIDADES_DE_PLAZO.map((u) => ({ value: u.value, label: u.label, hint: u.nota })),
            ]}
          />
        </Field>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          {/* Sin `placeholder` con un número y sin `defaultValue`: RN-12 dice que el
              sistema no trae ningún plazo, y un placeholder con "3" se copia. */}
          <Field label="Plazo para EA grave" hint="Vacío = esta autoridad no tiene plazo para casos graves" error={problemas.plazo_grave}>
            <Input
              type="number" min="0" inputMode="numeric"
              value={editando?.plazo_grave ?? ''}
              onChange={(e) => onCambio({ plazo_grave: e.target.value })}
            />
          </Field>
          <Field label="Plazo para EA no grave" hint="Vacío = sin plazo para casos no graves" error={problemas.plazo_no_grave}>
            <Input
              type="number" min="0" inputMode="numeric"
              value={editando?.plazo_no_grave ?? ''}
              onChange={(e) => onCambio({ plazo_no_grave: e.target.value })}
            />
          </Field>
        </div>

        <Field
          label="Avisar antes de vencer"
          hint="En la MISMA unidad que el plazo. Exige al menos un plazo configurado"
          error={problemas.umbral_alerta}
        >
          <Input
            type="number" min="0" inputMode="numeric"
            value={editando?.umbral_alerta ?? ''}
            onChange={(e) => onCambio({ umbral_alerta: e.target.value })}
          />
        </Field>

        {/* HU-23-C10 · EL SEGUNDO RELOJ. Corre en PARALELO al de notificación, desde el
            mismo día cero: notificar el día 3 no reinicia nada. Sin valor por defecto,
            como todo plazo (C2): el 15 de la Res. 2528 lo carga ILENDER a conciencia. */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <Field
            label="Plazo del informe final"
            hint="Res. 2528 §9.12 (b): el FAR-03 a la autoridad. Desde el MISMO día cero que la notificación, no después de ella. Vacío = sin configurar"
            error={problemas.plazo_informe_final}
          >
            <Input
              type="number" min="0" inputMode="numeric"
              value={editando?.plazo_informe_final ?? ''}
              onChange={(e) => onCambio({ plazo_informe_final: e.target.value })}
            />
          </Field>
          <Field
            label="Aviso previo del informe final"
            hint="En la misma unidad. Exige el plazo del informe final"
            error={problemas.umbral_informe_final}
          >
            <Input
              type="number" min="0" inputMode="numeric"
              value={editando?.umbral_informe_final ?? ''}
              onChange={(e) => onCambio({ umbral_informe_final: e.target.value })}
            />
          </Field>
        </div>

        <Field
          label="El plazo empieza a contar desde"
          hint="Res. 2528 Anexo I: el «día cero» es la recepción del reporte por ILENDER. «Presentación» solo donde una norma nacional desplace a la andina"
        >
          <Select
            sinVacio
            value={editando?.origen_computo ?? 'recepcion_ilender'}
            onChange={(e) => onCambio({ origen_computo: e.target.value })}
            options={ORIGENES_DE_COMPUTO.map((o) => ({ value: o.value, label: o.label, hint: o.nota }))}
          />
        </Field>

        {/* HU-23-C4 · RN-20. Los días hábiles se cuentan en la zona del país de la
            autoridad, no en la de Lima ni en la del servidor. Sin ella el cálculo se
            marca no fiable en vez de asumir UTC en silencio. Se elige de la lista del
            navegador; el campo de texto queda solo como respaldo para un navegador que
            no la exponga, y el dominio sigue validando lo que llegue. */}
        {delPais && (
        <Field
          label="Zona horaria de la autoridad"
          hint="La del país de la autoridad: decide qué día es el día cero para un reporte recibido de noche. Cada opción muestra la hora que es ahora en esa zona, para compararla con el reloj del país"
          error={problemas.zona_horaria}
        >
          {opcionesDeZona.length > 0 ? (
            <Select
              buscable
              placeholder="Sin configurar"
              value={zonaActual}
              onChange={(e) => onCambio({ zona_horaria: String(e.target.value ?? '') })}
              options={opcionesDeZona}
            />
          ) : (
            <Input
              value={zonaActual}
              onChange={(e) => onCambio({ zona_horaria: e.target.value })}
            />
          )}
        </Field>
        )}
        </fieldset>

        <fieldset className="border border-hairline rounded-xl p-4 space-y-4">
        <legend className="px-1.5 text-body font-semibold text-ink">Alertas internas</legend>
        {/* HU-23-C9 · los DOS umbrales de aviso. Son los únicos campos de esta pantalla
            que traen default (5 y 2): no son plazos ante nadie, solo deciden cuándo el
            sistema se avisa a sí mismo. Un plazo por defecto afirmaría una obligación
            legal; un umbral por defecto, como mucho, un correo de más. */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <Field
            label="Días para alertar acuse demorado"
            hint={ayudaDelAcuse(delPais)}
            error={problemas.dias_alerta_acuse_demorado}
          >
            <Input
              type="number" min="0" inputMode="numeric"
              value={editando?.dias_alerta_acuse_demorado ?? ''}
              onChange={(e) => onCambio({ dias_alerta_acuse_demorado: e.target.value })}
            />
          </Field>
          <Field
            label="Días para escalar vencido"
            hint="Días hábiles de vencido sin documento enviado tras los que se escribe al Gerente General. Vacío = vuelve a 2"
            error={problemas.dias_escalamiento_vencido}
          >
            <Input
              type="number" min="0" inputMode="numeric"
              value={editando?.dias_escalamiento_vencido ?? ''}
              onChange={(e) => onCambio({ dias_escalamiento_vencido: e.target.value })}
            />
          </Field>
        </div>
        </fieldset>

        <fieldset className="border border-hairline rounded-xl p-4 space-y-4">
        <legend className="px-1.5 text-body font-semibold text-ink">Firma y presentación</legend>
        <Field
          label="¿Exige la firma del Profesional Responsable?"
          hint="Decide la ruta de firma de la carta. En Perú firma y presenta Asuntos Regulatorios"
        >
          <Select
            sinVacio
            value={editando?.exige_profesional_responsable ?? 'si'}
            onChange={(e) => onCambio({ exige_profesional_responsable: e.target.value })}
            options={[
              { value: 'si', label: 'Sí: firma el PRFV y presenta el Representante del País' },
              { value: 'no', label: 'No: firma y presenta Asuntos Regulatorios' },
            ]}
          />
        </Field>

        <Field
          label="Firmante responsable (firma pre-configurada)"
          hint="Quien firma la carta donde la autoridad NO exige PRFV («Firmar y enviar»). Sin él, la pantalla lo pide."
        >
          <Input
            aria-label="Firmante responsable"
            value={editando?.firmante_responsable ?? ''}
            onChange={(e) => onCambio({ firmante_responsable: e.target.value })}
          />
        </Field>
        </fieldset>

        <fieldset className="border border-hairline rounded-xl p-4 space-y-4">
        <legend className="px-1.5 text-body font-semibold text-ink">Documento y plantilla</legend>
        {/* HU-23-C1 · «exige §11» y «plantilla». La sección 11 es del país (el Formato
            Amarillo es uno solo, CC-02): solo se edita desde la predeterminada. */}
        {delPais && (
        <Field
          label="¿Exige la sección 11 del Formato Amarillo?"
          hint="Datos de las personas afectadas. Decide si esa sección viaja a esta autoridad o se omite"
        >
          <Select
            sinVacio
            value={editando?.exige_seccion_de_personas ?? 'no'}
            onChange={(e) => onCambio({ exige_seccion_de_personas: e.target.value })}
            options={[
              { value: 'si', label: 'Sí: la autoridad pide los datos de personas' },
              { value: 'no', label: 'No: la sección 11 no se envía' },
            ]}
          />
        </Field>
        )}

        {/* La versión NO se teclea (21-sep-2026): la carta se prepara con la versión de la
            plantilla VIGENTE a la fecha (HU-45 / HU-20) y el servidor la devuelve ya resuelta.
            Un número escrito aquí no cambiaba lo que se emitía y desmentía al historial. */}
        <Field
          label="Plantilla de carta"
          hint="La versión vigente de la plantilla de esta autoridad. Se publica en Administración → Plantillas de carta; la carta usa la vigente en la fecha de emisión."
        >
          <Input
            readOnly aria-readonly="true"
            className="read-only:bg-slate-50 read-only:text-ink-soft read-only:cursor-default"
            value={textoDeLaPlantilla(editando)}
          />
        </Field>

        {delPais && (
        <Field
          label="Periodicidad del IPS"
          hint="Se guarda, pero todavía no se usa."
        >
          <Input
            value={editando?.periodicidad_ips ?? ''}
            onChange={(e) => onCambio({ periodicidad_ips: e.target.value })}
          />
        </Field>
        )}
        </fieldset>
      </div>
    </Modal>
  );
}

// El formato de pegado del calendario: una linea por feriado, `FECHA<tab o ;>NOMBRE`.
//
// Se elige texto pegable y no un alta fila a fila porque un calendario son 12-18 fechas
// que llegan de una publicacion oficial: doce formularios seguidos es donde se cometen
// los errores de dedo, y un pegado se puede revisar entero antes de enviarlo.
const LINEA_DE_FERIADO = /^\s*(\d{4}-\d{2}-\d{2})\s*[;,\t|]\s*(.+?)\s*$/;

/** Un feriado del calendario pegado: su fecha ISO y como se llama. */
export type FeriadoInterpretado = { fecha: string; descripcion: string };

export function interpretarFeriados(texto: string): { feriados: FeriadoInterpretado[]; errores: string[] } {
  const feriados: FeriadoInterpretado[] = [];
  const errores: string[] = [];
  const vistas = new Set();
  String(texto ?? '').split('\n').forEach((linea, indice) => {
    if (!linea.trim()) return;
    const encaje = LINEA_DE_FERIADO.exec(linea);
    if (!encaje) {
      errores.push(`Línea ${indice + 1}: se esperaba «AAAA-MM-DD ; Nombre del feriado».`);
      return;
    }
    const [, fecha, descripcion] = encaje;
    // Se comprueba que la fecha EXISTA, no solo que tenga forma: `2026-02-31` encaja con
    // la expresión y no es un día. Una fecha inventada en este calendario mueve el
    // vencimiento de una obligación regulatoria.
    const dia = new Date(`${fecha}T00:00:00Z`);
    if (Number.isNaN(dia.getTime()) || dia.toISOString().slice(0, 10) !== fecha) {
      errores.push(`Línea ${indice + 1}: «${fecha}» no es una fecha del calendario.`);
      return;
    }
    if (vistas.has(fecha)) {
      errores.push(`Línea ${indice + 1}: «${fecha}» está repetida.`);
      return;
    }
    vistas.add(fecha);
    feriados.push({ fecha, descripcion });
  });
  return { feriados, errores };
}

function ModalDeFeriados({ autoridad, onCerrar, onCargado }: {
  autoridad: RespuestaDelApi;
  onCerrar: () => void;
  onCargado: () => Promise<void> | void;
}) {
  const [calendario, setCalendario] = useState<RespuestaDelApi | null>(null);
  const [cargando, setCargando] = useState(false);
  const [texto, setTexto] = useState('');
  const [fuente, setFuente] = useState('');
  const [consultadoEl, setConsultadoEl] = useState('');
  const [enviando, setEnviando] = useState(false);
  const [error, setError] = useState<Fallo | null>(null);

  useEffect(() => {
    if (!autoridad) return;
    let vigente = true;
    setCargando(true);
    setError(null);
    setTexto('');
    api.listarFeriados(autoridad.pais)
      .then((r) => { if (vigente) setCalendario(r as RespuestaDelApi); })
      .catch((e) => { if (vigente) setError(aFallo(e)); })
      .finally(() => { if (vigente) setCargando(false); });
    return () => { vigente = false; };
  }, [autoridad]);

  const { feriados, errores } = useMemo(() => interpretarFeriados(texto), [texto]);

  const enviar = async () => {
    setEnviando(true);
    setError(null);
    try {
      const resultado = await api.cargarFeriados(autoridad.pais, {
        fuente: fuente.trim(),
        consultado_el: consultadoEl,
        feriados,
      });
      setCalendario(resultado);
      setTexto('');
      await onCargado();
    } catch (e) {
      setError(aFallo(e));
    } finally {
      setEnviando(false);
    }
  };

  const puedeEnviar =
    feriados.length > 0 && errores.length === 0 && fuente.trim().length >= 5 && Boolean(consultadoEl);

  return (
    <Modal
      open={Boolean(autoridad)}
      onClose={onCerrar}
      eyebrow="Administración · Autoridades y plazos"
      title={autoridad ? `Calendario hábil de ${autoridad.nombre_pais ?? autoridad.pais}` : 'Calendario hábil'}
      icon={CalendarDays}
      footer={
        <>
          <Button variant="secondary" onClick={onCerrar}>Cerrar</Button>
          <Button variant="primary" icon={Plus} onClick={enviar} disabled={!puedeEnviar || enviando}>
            {enviando ? 'Cargando…' : `Cargar ${feriados.length || ''} feriado(s)`}
          </Button>
        </>
      }
    >
      {error && <Alert tone="danger" title="No se pudo completar la operación" detalleTecnico={error.detalleTecnico}>{error.message}</Alert>}

      <Alert tone="info" title="La carga no borra: corrige y añade">
        Volver a cargar una fecha la corrige. Para retirar un feriado ya cargado, pídelo a soporte.
      </Alert>

      <div className="mt-3">
        {cargando && <p className="text-body text-ink-soft">Cargando calendario…</p>}
        {!cargando && calendario && (
          <>
            <p className="text-meta text-ink-soft mb-2">
              Años cargados:{' '}
              {calendario.anios_cargados?.length
                ? <Mono>{calendario.anios_cargados.join(', ')}</Mono>
                : <em>ninguno: el cálculo en días hábiles no es fiable</em>}
            </p>
            {calendario.feriados?.length > 0 && (
              <div className="max-h-44 overflow-y-auto border border-hairline rounded-xl">
                <table className="w-full text-meta">
                  <tbody>
                    {calendario.feriados.map((f: FeriadoInterpretado & { fuente?: string }) => (
                      <tr key={f.fecha} className="border-b border-hairline last:border-0">
                        <td className="px-3 py-1.5"><Mono>{f.fecha}</Mono></td>
                        <td className="px-3 py-1.5">{f.descripcion}</td>
                        <td className="px-3 py-1.5 text-ink-soft text-meta">{f.fuente}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </>
        )}
      </div>

      <div className="space-y-4 mt-4">
        <Field
          label="Fuente oficial"
          required
          hint="Norma, gaceta o publicación del ministerio. Sin fuente no es un calendario oficial, es una lista"
        >
          <Input value={fuente} onChange={(e) => setFuente(e.target.value)} />
        </Field>
        <Field label="Fecha de consulta de la fuente" required>
          <Input type="date" value={consultadoEl} onChange={(e) => setConsultadoEl(e.target.value)} />
        </Field>
        <Field
          label="Feriados"
          required
          hint="Una línea por feriado: AAAA-MM-DD ; Nombre"
          error={errores[0]}
        >
          <Textarea
            rows={7}
            value={texto}
            onChange={(e) => setTexto(e.target.value)}
            placeholder={'2026-01-01 ; Año Nuevo\n2026-04-03 ; Viernes Santo'}
          />
        </Field>
        {errores.length > 1 && (
          <Alert tone="warning" title={`${errores.length} líneas no se pudieron leer`} icon={Clock}>
            <ul className="list-disc pl-4">
              {errores.slice(0, 5).map((e) => <li key={e}>{e}</li>)}
            </ul>
          </Alert>
        )}
      </div>
    </Modal>
  );
}
